<template>
  <div
    :style="getBackground"
    class="app-section-wrap"
  >
    <section class="app-section">
      <div class="texts">
        <h2 class="section-title">{{ section.title }}</h2>
        <div
          v-if="section.description && !isMobile"
          v-dompurify-html="section.description"
          class="description"
        />

        <div
          v-if="isDesktop"
          class="qr-text"
        >
          {{ $t('indexAd.scanQr') }}
        </div>
        <div
          v-if="showButtons"
          class="app-wrapper__stores"
        >
          <div class="buttons">
            <div class="button">
              <a
                v-if="section.appstore.show"
                :href="section.appstore.link.href"
                :target="section.appstore.link.target"
              >
                <v-lazy-image
                  :src="appStore"
                  alt="App store logo"
                  class="logo"
                  height="30"
                  width="92"
                />
              </a>
            </div>

            <div class="button">
              <a
                v-if="section.playstore.show"
                :href="section.playstore.link.href"
                :target="section.playstore.link.target"
              >
                <v-lazy-image
                  :src="playStore"
                  alt="Play store logo"
                  class="logo"
                  height="30"
                  width="103"
                />
              </a>
            </div>
          </div>
          <v-lazy-image
            :src="universalStoreQRCode"
            alt="Store QR code"
            class="qr-code"
            height="83"
            width="83"
          />
        </div>
      </div>
      <div class="image">
        <v-lazy-image
          :src="getBackgroundImage(section.images)"
          alt="Phone with airBaltic app"
          height="190"
          width="190"
        />
      </div>
    </section>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import VLazyImage from 'v-lazy-image'
  import ImageHelpers from 'utils/mixins/ImageHelpers'
  import get from 'lodash-es/get'

  export default {
    name: 'AdSection',
    components: {
      VLazyImage
    },
    mixins: [ImageHelpers],
    props: {
      section: {
        type: Object,
        required: true
      }
    },
    computed: {
      ...mapGetters('device', ['isMobile', 'isTablet', 'isDesktop']),
      getBackground() {
        let bg = `background: linear-gradient(180deg, ${this.section.colors.dark} 0%, ${this.section.colors.light} 100%)`

        if (this.isTablet) {
          bg = `background: ${this.section.colors.dark}`
        }

        if (this.isDesktop) {
          bg = `background: linear-gradient(158deg, ${this.section.colors.light} 0%, ${this.section.colors.dark} 100%)`
        }

        return bg
      },
      showButtons() {
        return this.section.appstore.show || this.section.playstore.show
      },
      appStore() {
        return require('assets/images/download/appstore.svg')
      },
      playStore() {
        return require('assets/images/download/playstore.svg')
      },
      universalStoreQRCode() {
        return require('assets/images/download/app-redirect-qr.png')
      },
      clubSvg() {
        return require('assets/images/menu/airbaltic_club.svg')
      }
    },
    methods: {
      getBackgroundImage(images) {
        if (images.length === 0) {
          return ''
        }

        const data = {
          desktop: get(images, 'desktopImg.source', ''),
          tablet: get(images, 'tabletImg.source', ''),
          mobile: get(images, 'mobileImg.source', '')
        }

        return this.$_ImageHelpers_responsiveImage(data)
      }
    }
  }
</script>

<style
  lang="scss"
  rel="stylesheet/scss"
  scoped
>
  @import '~assets/styles/common.scss';

  .app-section-wrap {
    margin: 20px 12px 28px;
    border-radius: 6px;

    @include bp-mf-tablet {
      margin: 40px 0;
      border-radius: 0;
    }
  }

  .app-section {
    min-height: 500px;
    display: flex;
    flex-direction: column;
    max-width: $desktop-wide-width;
    padding: 28px 24px;

    @include bp-mf-tablet {
      margin: 0;
      min-height: 400px;
      border-radius: 0;
      flex-direction: row;
      padding: 0;
      overflow: hidden;
    }

    @include bp-mf-desktop {
      margin: 0 auto;
      min-height: 460px;
      justify-content: center;
    }

    @include bp-desktop-small {
      margin: 0 26px;
    }
  }

  .section-title {
    @include defaultFontValues($blue-dark, 24, 32, 500);
    text-align: center;
    margin: 0 0 20px;

    @include bp-mf-tablet {
      margin-bottom: 14px;
      text-align: left;
    }

    @include bp-mf-desktop {
      font-size: em(32);
      line-height: 40px;
      margin-bottom: 24px;
    }
  }

  .texts {
    order: 1;

    @include bp-mf-tablet {
      order: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-left: 35px;
    }

    @include bp-mf-desktop {
      margin: 55px 35px 59px 0;
      width: 30%;
    }
  }

  .description {
    @include defaultFontValues($blue-dark, 14, 20);
    margin-bottom: 40px;
    width: 80%;

    ::v-deep p {
      margin: 0 0 8px;
    }

    @include bp-mf-desktop {
      margin-bottom: 37px;
      width: 100%;
    }
  }

  .qr-text {
    @include defaultFontValues($blue-dark, 14, 20, 500);
    margin-bottom: 25px;
  }

  .image {
    order: 0;
    width: 206px;
    height: 206px;
    background: linear-gradient(132.36deg, #e8ef61 0%, #cdda32 100%);
    border-radius: 50%;
    margin: 0 auto 22px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    overflow: hidden;

    @include bp-mf-tablet {
      order: 1;
      min-width: 435px;
      height: auto;
      background: transparent;
      border-radius: 0;
      justify-content: flex-end;
      align-items: flex-end;
      margin: 0;
    }

    @include bp-mf-desktop {
      min-width: 475px;
    }

    .v-lazy-image {
      height: 190px;
      margin-left: 24px;

      @include bp-mf-tablet {
        height: auto;
        width: 100%;
        margin: 0;
      }
    }
  }

  .app-wrapper__stores {
    display: flex;
    align-items: center;
    flex-direction: column;

    @include bp-desktop {
      gap: 34px;
      flex-direction: row;
    }
  }

  .buttons {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 40px;

    @include bp-desktop {
      flex-direction: column;
      gap: unset;
    }

    .button {
      display: flex;
      flex-direction: column;
      margin-bottom: 20px;
      align-items: center;

      .qr-code {
        margin-top: 10px;
        width: 55px;
        height: 55px;
      }

      &:last-child {
        margin-bottom: 0;
      }

      a {
        .logo {
          height: 30px;
          width: auto;
        }
      }
    }
  }
</style>
